import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Download, Loader2, RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  ivrSwitchSyncService, driftTone, worstDrift,
  type CatalogApp, type DriftRow, type PushAudit,
} from '../../api/services/ivrSwitchSync.service';

/**
 * IVR switch sync (docs/design/2026-10-14_ivr_app_management.md, Phases 0-2).
 *
 * Master: import both switches (dry run first), file owners, run the drift check
 * (flag only — nothing is adopted or reverted), and see / roll back pushes.
 * Everyone else ("My IVR apps"): the flows filed to their organisation, with whether
 * they can manage each one (own numbers only). A manageable flow opens in the editor
 * (/telephony/ivr/app: draft → validate → preview → publish → rollback, Phase 3).
 */
type Tab = 'catalog' | 'drift' | 'pushes';

const IvrSwitchSync: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = ((currentOrganization as any)?.role || (user as any)?.role || '').toLowerCase();
  const isMasterAdmin = role === 'master_admin';

  const [tab, setTab] = useState<Tab>('catalog');
  const [apps, setApps] = useState<CatalogApp[]>([]);
  const [drift, setDrift] = useState<DriftRow[]>([]);
  const [pushes, setPushes] = useState<PushAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [importPlan, setImportPlan] = useState<Record<string, any> | null>(null);
  const [rollbackPlan, setRollbackPlan] = useState<Record<string, any> | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!isMasterAdmin) {
      const res = await ivrSwitchSyncService.myFlows(currentOrganization?.id);
      if (res.success) setApps(res.data || []); else setError(res.error || 'Could not load');
      setLoading(false);
      return;
    }
    const [c, d, p] = await Promise.all([
      ivrSwitchSyncService.catalog(), ivrSwitchSyncService.drift(), ivrSwitchSyncService.pushes(),
    ]);
    if (c.success) setApps(c.data || []); else setError(c.error || 'Could not load');
    if (d.success) setDrift(d.data || []);
    if (p.success) setPushes(p.data || []);
    setLoading(false);
  }, [isMasterAdmin, currentOrganization?.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (label: string, fn: () => Promise<{ success: boolean; error?: string; data?: any }>,
                     after?: (data: any) => void) => {
    setBusy(label);
    setError(null);
    setNotice(null);
    const res = await fn();
    setBusy(null);
    if (!res.success) { setError(res.error || `${label} failed`); return; }
    after?.(res.data);
  };

  const shown = apps.filter(a => !filter
    || `${a.operatorId}/${a.serviceId} ${a.ownership?.ownerOrgName || ''}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{isMasterAdmin ? 'IVR switch sync' : 'My IVR apps'}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isMasterAdmin
              ? 'The flows the switches are running, imported read-only, with owners and drift. Drift is flagged, never fixed automatically.'
              : 'The IVR flows filed to your organisation. Open one to edit it as a draft and publish it; flows shared with another organisation stay with Green Giraffe.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {isMasterAdmin && (
            <>
              <button
                disabled={!!busy}
                onClick={() => run('Import preview', () => ivrSwitchSyncService.importAll(false), setImportPlan)}
                className="px-3 py-2 border border-blue-600 text-blue-700 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Import from switches…
              </button>
              <button
                disabled={!!busy}
                onClick={() => run('Drift check', () => ivrSwitchSyncService.checkDrift(undefined, undefined, 500),
                  () => { setNotice('Drift check finished.'); load(); })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" /> Check drift now
              </button>
              <button
                disabled={!!busy}
                onClick={() => run('Find unimported', () => ivrSwitchSyncService.findUnimported(),
                  d => { setNotice(`${d?.notImported ?? 0} flow(s) on the switches are not imported yet.`); load(); })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              >
                Find unimported flows
              </button>
            </>
          )}
        </div>
      </div>

      {busy && <p className="text-sm text-gray-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {busy}…</p>}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex gap-2" role="alert">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}
      {notice && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">{notice}</div>}

      {importPlan && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
          <h4 className="font-medium text-blue-900">
            Import {importPlan.applied ? 'done' : 'preview — nothing stored yet'}
          </h4>
          <ul className="text-xs text-blue-900 space-y-0.5">
            {(importPlan.providers || []).map((p: any) => (
              <li key={p.providerId}>
                {p.provider}: {p.status}{p.flows != null ? ` · ${p.flows} flow(s)` : ''}
                {p.numbers != null ? ` · ${p.numbers} number(s)` : ''}{p.error ? ` · ${p.error}` : ''}
                {p.numbersError ? ` · ${p.numbersError}` : ''}
              </li>
            ))}
          </ul>
          <p className="text-xs text-blue-900">
            {importPlan.flowVersions} flow version(s): {importPlan.newOrChanged} new or changed, {importPlan.unchanged} unchanged.
            {' '}{importPlan.unassigned} app(s) left unassigned for you to file.
          </p>
          <div className="flex gap-2">
            {!importPlan.applied && (
              <button
                disabled={!!busy}
                onClick={() => run('Import', () => ivrSwitchSyncService.importAll(true),
                  d => { setImportPlan(d); load(); })}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
              >
                Store this import
              </button>
            )}
            <button onClick={() => setImportPlan(null)} className="px-3 py-1.5 border border-gray-300 rounded text-xs">Close</button>
          </div>
        </div>
      )}

      {isMasterAdmin && (
        <div className="flex gap-4 border-b border-gray-200 text-sm">
          {(['catalog', 'drift', 'pushes'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-2 border-b-2 ${tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'}`}
            >
              {t === 'catalog' ? `Flows (${apps.length})` : t === 'drift' ? `Drift (${drift.filter(d => d.status === 'DRIFTED').length} drifted)` : `Pushes (${pushes.length})`}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : tab === 'catalog' || !isMasterAdmin ? (
        <div className="space-y-2">
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by operator / service / owner"
            className="w-full sm:w-80 border border-gray-300 rounded px-3 py-1.5 text-sm"
          />
          {shown.length === 0 && (
            <p className="text-sm text-gray-500">
              {isMasterAdmin ? 'Nothing imported yet. Run "Import from switches…".' : 'No IVR flow is filed to your organisation yet.'}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Flow</th>
                  <th className="py-2 pr-3">Owner</th>
                  <th className="py-2 pr-3">Switch · language · version</th>
                  <th className="py-2 pr-3">Recording folder</th>
                  <th className="py-2 pr-3">Drift</th>
                  {isMasterAdmin && <th className="py-2">File</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map(a => (
                  <tr key={`${a.operatorId}|${a.serviceId}`} className="border-b align-top">
                    <td className="py-2 pr-3 font-mono text-xs">
                      <div>{a.operatorId}/{a.serviceId}</div>
                      {(isMasterAdmin || a.manageable) && (
                        <Link className="font-sans text-blue-700"
                          to={`/telephony/ivr/app?op=${encodeURIComponent(a.operatorId)}&svc=${encodeURIComponent(a.serviceId)}`}>
                          {isMasterAdmin ? 'Open' : 'Edit / publish'}
                        </Link>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      <div>{a.ownership?.ownerOrgName || a.ownership?.ownerOrgId || '—'}</div>
                      <div className="text-gray-500">{a.ownership?.status}</div>
                      {a.ownership?.reason && <div className="text-gray-400">{a.ownership.reason}</div>}
                      {a.manageable != null && (
                        <div className={a.manageable ? 'text-green-700' : 'text-gray-500'}>{a.manageReason}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {a.flows.map(f => (
                        <div key={f.versionId}>
                          {f.provider || `provider ${f.providerId}`} · {f.language || '(none)'} · v{f.versionNo}
                          {' '}· {f.stateCount} states{f.duplicateKeys ? ` · duplicates: ${f.duplicateKeys}` : ''}
                        </div>
                      ))}
                    </td>
                    <td className="py-2 pr-3 text-xs font-mono">
                      {Array.from(new Set(a.flows.map(f => f.businessNames).filter(Boolean))).join(', ') || '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${driftTone(worstDrift(a.flows))}`}>{worstDrift(a.flows)}</span>
                      {isMasterAdmin && (
                        <button
                          className="block text-xs text-blue-700 mt-1"
                          disabled={!!busy}
                          onClick={() => run('Drift check', () => ivrSwitchSyncService.checkDrift(a.operatorId, a.serviceId), () => load())}
                        >
                          Check now
                        </button>
                      )}
                    </td>
                    {isMasterAdmin && (
                      <td className="py-2 text-xs">
                        <button
                          className="text-blue-700"
                          disabled={!!busy}
                          onClick={() => {
                            const org = window.prompt(
                              `Organisation id to own ${a.operatorId}/${a.serviceId} (blank = unassign; numbers suggest ${a.ownership?.suggestedOrgName || a.ownership?.suggestedOrgId || 'nobody'})`,
                              a.ownership?.ownerOrgId || a.ownership?.suggestedOrgId || '');
                            if (org === null) return;
                            run('File owner', () => ivrSwitchSyncService.assignOwner(a.operatorId, a.serviceId, org.trim() || undefined), () => load());
                          }}
                        >
                          Assign…
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : tab === 'drift' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Flow</th>
                <th className="py-2 pr-3">Switch · language</th>
                <th className="py-2 pr-3">What differs</th>
                <th className="py-2">Checked</th>
              </tr>
            </thead>
            <tbody>
              {drift.map(d => (
                <tr key={`${d.providerId}|${d.operatorId}|${d.serviceId}|${d.language}`} className="border-b align-top">
                  <td className="py-2 pr-3"><span className={`text-xs px-2 py-0.5 rounded ${driftTone(d.status)}`}>{d.status}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs">{d.operatorId}/{d.serviceId}</td>
                  <td className="py-2 pr-3 text-xs">{d.provider || d.providerId} · {d.language}</td>
                  <td className="py-2 pr-3 text-xs">
                    {d.report?.added?.length ? <div>Added on switch: {d.report.added.join(', ')}</div> : null}
                    {d.report?.removed?.length ? <div>Gone from switch: {d.report.removed.join(', ')}</div> : null}
                    {d.report?.changed?.map(c => (
                      <div key={c.key}>Changed {c.key}: {Object.keys(c.diff).join(', ')}</div>
                    ))}
                    {d.businessNameChanged ? (
                      <div className="text-red-700">
                        Recording folder {(d.report?.businessNamesBefore || []).join(',')} → {(d.report?.businessNamesNow || []).join(',')}
                      </div>
                    ) : null}
                    {d.report?.duplicates?.length ? <div className="text-amber-700">Duplicate rows: {d.report.duplicates.join(', ')}</div> : null}
                    {d.report?.error ? <div className="text-amber-700">{d.report.error}</div> : null}
                    {d.report?.note ? <div>{d.report.note}</div> : null}
                  </td>
                  <td className="py-2 text-xs text-gray-500">{d.checkedAt?.replace('T', ' ').slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {drift.length === 0 && <p className="text-sm text-gray-500 mt-2">No drift check has run yet.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {rollbackPlan && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-1">
              <div className="font-medium text-amber-900">
                Roll back push #{rollbackPlan.pushId} on {rollbackPlan.provider} — {rollbackPlan.applied ? 'done' : 'preview, nothing written'}
              </div>
              <div>Restores {rollbackPlan.restore?.length ?? 0} state(s): {(rollbackPlan.restore || []).map((r: any) => r.name).join(', ') || '—'}</div>
              <div>States the push added (kept unless removed after the 10-minute grace): {(rollbackPlan.insertedByPush || []).map((r: any) => r.name).join(', ') || '—'}</div>
              {(rollbackPlan.blockers || []).map((b: string, i: number) => <div key={i} className="text-red-700">{b}</div>)}
              <div className="flex gap-2 pt-1">
                {!rollbackPlan.applied && (
                  <button
                    className="tl-btn tl-btn-primary tl-btn-sm"
                    disabled={!!busy || !!rollbackPlan.blockers?.length || rollbackPlan.applyEnabled === false}
                    onClick={() => run('Rollback', () => ivrSwitchSyncService.rollback(rollbackPlan.pushId, true, false, rollbackPlan.previewHash),
                      d => { setRollbackPlan(d); load(); })}
                  >
                    Write the old rows back
                  </button>
                )}
                <button className="px-3 py-1.5 border border-gray-300 rounded" onClick={() => setRollbackPlan(null)}>Close</button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">When · who</th>
                  <th className="py-2 pr-3">Flow</th>
                  <th className="py-2 pr-3">Recording folder</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {pushes.map(p => (
                  <tr key={p.id} className="border-b align-top text-xs">
                    <td className="py-2 pr-3">{p.id}{p.kind === 'ROLLBACK' ? ` (rollback of #${p.rollbackOfId})` : ''}</td>
                    <td className="py-2 pr-3">{p.pushedAt?.replace('T', ' ').slice(0, 16)} · {p.pushedBy}</td>
                    <td className="py-2 pr-3 font-mono">{p.operatorId}/{p.serviceId} · {p.language} · {p.provider}</td>
                    <td className="py-2 pr-3 font-mono">
                      {p.businessNameBefore === p.businessNameAfter ? p.businessNameAfter : `${p.businessNameBefore} → ${p.businessNameAfter}`}
                    </td>
                    <td className="py-2 pr-3">{p.result} · {p.rowsWritten ?? 0} written{p.rolledBackById ? ` · rolled back by #${p.rolledBackById}` : ''}</td>
                    <td className="py-2">
                      {p.kind === 'PUSH' && !p.rolledBackById && (
                        <button
                          className="text-blue-700 flex items-center gap-1"
                          disabled={!!busy}
                          onClick={() => run('Rollback preview', () => ivrSwitchSyncService.rollback(p.id, false), setRollbackPlan)}
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Roll back…
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pushes.length === 0 && <p className="text-sm text-gray-500 mt-2">No switch push has been recorded yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default IvrSwitchSync;
